import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, ChatCircleDots } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const when = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium" }) : "";

// Staff-side view of the customer portal thread on a quote or a job
export const PortalThread = ({ kind, recordId }) => {
  const base =
    kind === "quote" ? "quotes" : kind === "project" ? "projects" : "jobs";
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!recordId) return;
    try {
      const { data } = await api.get(`/${base}/${recordId}/portal-messages`);
      setItems(data);
    } catch {
      setItems([]);
    }
  }, [base, recordId]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${base}/${recordId}/portal-messages`, {
        body: text.trim(),
      });
      setItems(data);
      setText("");
      toast.success("Reply sent to the customer");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send that reply",
      );
    }
    setBusy(false);
  };

  if (!recordId) return null;

  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
      data-testid="portal-thread-card"
    >
      <div className="flex items-center gap-2">
        <ChatCircleDots size={16} weight="bold" className="text-indigo-300" />
        <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-300">
          Customer portal notes
        </h2>
        <span
          className="ml-auto font-mono-data text-[11px] text-zinc-500"
          data-testid="portal-thread-count"
        >
          {items.length}
        </span>
      </div>
      <div className="mt-4 space-y-2">
        {items.length === 0 && (
          <p
            className="text-xs text-zinc-500"
            data-testid="portal-thread-empty"
          >
            No notes from the customer yet.
          </p>
        )}
        {items.map((m) => (
          <div
            key={m.id}
            data-testid={`portal-thread-message-${m.id}`}
            className={`rounded-lg border p-3 text-sm ${
              m.author === "customer"
                ? "border-indigo-500/30 bg-indigo-500/10 text-zinc-100"
                : "border-zinc-800 bg-zinc-950 text-zinc-300"
            }`}
          >
            <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500">
              {m.author === "customer" ? "Customer" : "You"} ·{" "}
              {m.author_name || ""} · {when(m.created_at)}
              {m.subject_code ? ` · ${m.subject_code}` : ""}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <textarea
          data-testid="portal-thread-input"
          value={text}
          rows={2}
          onChange={(e) => setText(e.target.value)}
          placeholder="Reply to the customer…"
          className="min-h-[48px] flex-1 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400"
        />
        <button
          type="button"
          data-testid="portal-thread-send"
          disabled={busy || !text.trim()}
          onClick={send}
          className="flex h-12 items-center justify-center gap-2 rounded-lg bg-indigo-500 px-5 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
        >
          <PaperPlaneTilt size={14} weight="bold" />
          {busy ? "Sending…" : "Reply"}
        </button>
      </div>
    </div>
  );
};
